import { useState, useEffect, useCallback, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiShield, FiTruck, FiRefreshCw, FiStar, FiChevronRight, FiHeadphones, FiMail, FiCheck } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getCategories, getProducts, getVendors } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import './HomePage.css';

const HERO_CARDS = [
  { seed: 'tech',    label: 'Electronics',   offset: false },
  { seed: 'fashion', label: 'Fashion',        offset: true  },
  { seed: 'home',    label: 'Home & Living',  offset: false },
  { seed: 'beauty',  label: 'Beauty',         offset: true  },
];

const HERO_STATS = [
  { value: '50K+',  label: 'Products' },
  { value: '500+',  label: 'Verified Vendors' },
  { value: '100K+', label: 'Happy Customers' },
];

const FEATURES = [
  { icon: <FiTruck size={22} />,      title: 'Free Shipping',  desc: 'Free delivery on orders over Rs. 500.',       iconBg: '#dbeafe', iconColor: '#2563eb' },
  { icon: <FiShield size={22} />,     title: 'Secure Payment', desc: 'Every transaction is encrypted.',           iconBg: '#dcfce7', iconColor: '#16a34a' },
  { icon: <FiRefreshCw size={22} />,  title: 'Easy Returns',   desc: '30-day hassle-free return policy.',         iconBg: '#fef3c7', iconColor: '#d97706' },
  { icon: <FiHeadphones size={22} />, title: '24/7 Support',   desc: 'Round-the-clock customer support.',        iconBg: '#ede9ff', iconColor: '#6c47ff' },
];

const DEMO_CATEGORIES = [
  { _id: 'c1', name: 'Electronics',   icon: '📱', slug: 'electronics',   color: '#ede9ff' },
  { _id: 'c2', name: 'Fashion',       icon: '👗', slug: 'fashion',       color: '#fce7f3' },
  { _id: 'c3', name: 'Home & Living', icon: '🏠', slug: 'home-living',   color: '#e0faf5' },
  { _id: 'c4', name: 'Beauty',        icon: '💄', slug: 'beauty',        color: '#fee2e2' },
  { _id: 'c5', name: 'Sports',        icon: '⚽', slug: 'sports',        color: '#fef3c7' },
  { _id: 'c6', name: 'Books',         icon: '📚', slug: 'books',         color: '#dbeafe' },
  { _id: 'c7', name: 'Toys',          icon: '🎮', slug: 'toys',          color: '#f0fdf4' },
  { _id: 'c8', name: 'Handmade',      icon: '🎨', slug: 'handmade',      color: '#fff7ed' },
];

const CATEGORY_COLORS = ['#ede9ff','#fce7f3','#e0faf5','#fee2e2','#fef3c7','#dbeafe','#f0fdf4','#fff7ed'];


const STEPS_BUYERS = [
  { icon: '🔍', title: 'Browse',      desc: 'Explore thousands of products from verified vendors' },
  { icon: '🛒', title: 'Add to Cart', desc: 'Select the items you love with one click' },
  { icon: '💳', title: 'Checkout',    desc: 'Secure payment completed in seconds' },
  { icon: '📦', title: 'Receive',     desc: 'Fast delivery straight to your door' },
];

const STEPS_SELLERS = [
  { icon: '📝', title: 'Apply',        desc: 'Fill out a simple vendor application form' },
  { icon: '✅', title: 'Get Approved', desc: 'Our team reviews your store in 24 hours' },
  { icon: '📦', title: 'List Products', desc: 'Upload your catalog and start showcasing' },
  { icon: '💰', title: 'Get Paid',     desc: 'Earn on every sale, withdraw anytime' },
];

const TESTIMONIALS = [
  {
    id: 1,
    name: 'Ayesha Malik',
    role: 'Verified Buyer',
    avatar: 'woman1',
    rating: 5,
    quote: 'Fivora completely changed how I shop online. The vendors are reliable, delivery is fast, and products are exactly as described. I\'ve recommended it to all my friends!',
  },
  {
    id: 2,
    name: 'Omar Farooq',
    role: 'Vendor Partner',
    avatar: 'man1',
    rating: 5,
    quote: 'Since joining as a vendor, my sales have tripled. The dashboard is intuitive, customer reach is incredible, and the support team is always ready to help.',
  },
  {
    id: 3,
    name: 'Sana Ahmed',
    role: 'Verified Buyer',
    avatar: 'woman2',
    rating: 5,
    quote: 'Love the variety of products here. Found amazing deals on electronics and home décor. The returns process was smooth and hassle-free. Highly recommended!',
  },
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('buyers');
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [featuredRes, newArrivalsRes, catsRes, vendorsRes] = await Promise.allSettled([
        getProducts({ limit: 8, featured: true, status: 'active' }),
        getProducts({ limit: 8, sort: '-createdAt', status: 'active' }),
        getCategories({ limit: 8, isActive: true }),
        getVendors({ limit: 4, status: 'approved', sort: '-rating' }),
      ]);

      if (featuredRes.status === 'fulfilled') {
        setFeaturedProducts(featuredRes.value.data.products || []);
      }
      if (newArrivalsRes.status === 'fulfilled') {
        setNewArrivals(newArrivalsRes.value.data.products || []);
      }
      if (catsRes.status === 'fulfilled') {
        setCategories(catsRes.value.data.categories || []);
      }
      if (vendorsRes.status === 'fulfilled') {
        setTopVendors(vendorsRes.value.data.vendors || []);
      }
    } catch {
      // fallback: leave empty so demo data shows
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const displayCategories = categories.length > 0 ? categories : DEMO_CATEGORIES;
  const displayProducts = featuredProducts.slice(0, 8);
  const displayNewArrivals = newArrivals.slice(0, 8);
  const displayVendors = topVendors.slice(0, 4);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    toast.success('Subscribed! Welcome to the Fivora community.');
    setNewsletterEmail('');
  };

  return (
    <div className="home-page">

      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-inner container">

          {/* Left: content */}
          <div className="hero-content">
            <span className="hero-pill">✨ Trusted by 100,000+ shoppers</span>

            <h1 className="hero-heading">
              Shop Smarter,{' '}
              <br className="hero-br" />
              Sell <span className="hero-gradient-text">Better</span>
            </h1>

            <p className="hero-sub">
              Pakistan's premier marketplace connecting buyers with 500+ verified vendors.
              Discover authentic products with secure payments and fast delivery.
            </p>

            <div className="hero-ctas">
              <Link to="/products" className="btn btn-primary btn-lg hero-cta-primary">
                Shop Now <FiArrowRight size={17} />
              </Link>
              <Link to="/vendor/apply" className="hero-cta-outline btn-lg">
                Start Selling
              </Link>
            </div>

            <div className="hero-stats">
              {HERO_STATS.map(({ value, label }, i) => (
                <div key={label} className="hero-stat-group">
                  {i > 0 && <span className="hero-stat-divider" aria-hidden="true" />}
                  <div className="hero-stat">
                    <span className="hero-stat-value">{value}</span>
                    <span className="hero-stat-label">{label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: floating product cards */}
          <div className="hero-visual" aria-hidden="true">
            <div className="hero-cards-grid">
              {HERO_CARDS.map((card, i) => (
                <div
                  key={card.seed}
                  className={`hero-card${card.offset ? ' hero-card--down' : ''}`}
                  style={{ '--card-delay': `${0.45 + i * 0.15}s` }}
                >
                  {i === 0 && (
                    <span className="hero-card-badge">🔥 Trending</span>
                  )}
                  <div className="hero-card-img-wrap">
                    <img
                      src={`https://picsum.photos/seed/${card.seed}/200/200`}
                      alt={card.label}
                      className="hero-card-img"
                      loading="lazy"
                    />
                  </div>
                  <p className="hero-card-label">{card.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────────────── */}
      <section className="home-features">
        <div className="container">
          <div className="features-grid">
            {FEATURES.map(({ icon, title, desc, iconBg, iconColor }, i) => (
              <motion.div
                key={title}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="feature-icon" style={{ background: iconBg, color: iconColor }}>{icon}</div>
                <div>
                  <h4 className="feature-title">{title}</h4>
                  <p className="feature-desc">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Categories ────────────────────────────────────────────────────────── */}
      <section className="home-section">
        <div className="container">
          <div className="home-section-header">
            <div>
              <h2 className="section-title">Shop by Category</h2>
              <p className="section-subtitle">Find everything you need in one place</p>
            </div>
            <Link to="/products" className="see-all-link">
              View All <FiChevronRight size={14} />
            </Link>
          </div>

          <div className="categories-grid">
            {displayCategories.slice(0, 8).map((cat, i) => (
              <motion.div
                key={cat._id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <Link
                  to={`/products?category=${cat.slug || cat._id}`}
                  className="category-card"
                  style={{ '--cat-bg': cat.color || CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                >
                  {cat.image ? (
                    <img src={`http://localhost:5000${cat.image}`} alt={cat.name} className="cat-image" />
                  ) : (
                    <span className="cat-icon">{cat.icon || '🛍️'}</span>
                  )}
                  <span className="cat-name">{cat.name}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Products ─────────────────────────────────────────────────── */}
      <section className="home-section home-section-alt">
        <div className="container">
          <div className="home-section-header">
            <div>
              <h2 className="section-title">Featured Products</h2>
              <p className="section-subtitle">Handpicked by our team</p>
            </div>
            <Link to="/products" className="see-all-link">
              View All <FiChevronRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="products-grid">
              {[...Array(4)].map((_, i) => <div key={i} className="product-skeleton" />)}
            </div>
          ) : (
            <div className="products-grid">
              {displayProducts.map((product, i) => (
                <motion.div
                  key={product._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Flash Sale Banner ─────────────────────────────────────────────────── */}
      <section className="flash-sale-banner">
        <div className="container">
          <motion.div
            className="flash-sale-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flash-sale-left">
              <span className="flash-sale-badge">⚡ Flash Sale</span>
              <h2 className="flash-sale-heading">Up to <span>70% Off</span></h2>
              <p className="flash-sale-sub">Today only — limited stock!</p>
              <Link to="/products?sale=true" className="flash-sale-btn">
                Shop Flash Sale <FiArrowRight size={16} />
              </Link>
            </div>
            <div className="flash-sale-countdown">
              <div className="countdown-unit">
                <span className="countdown-num">04</span>
                <span className="countdown-label">Hours</span>
              </div>
              <span className="countdown-sep">:</span>
              <div className="countdown-unit">
                <span className="countdown-num">23</span>
                <span className="countdown-label">Mins</span>
              </div>
              <span className="countdown-sep">:</span>
              <div className="countdown-unit">
                <span className="countdown-num">11</span>
                <span className="countdown-label">Secs</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── New Arrivals ──────────────────────────────────────────────────────── */}
      <section className="home-section">
        <div className="container">
          <div className="home-section-header">
            <div>
              <h2 className="section-title">New Arrivals</h2>
              <p className="section-subtitle">Fresh products added this week</p>
            </div>
            <Link to="/products?sort=newest" className="see-all-link">
              View All <FiChevronRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="products-grid">
              {[...Array(4)].map((_, i) => <div key={i} className="product-skeleton" />)}
            </div>
          ) : (
            <div className="products-grid">
              {displayNewArrivals.map((product, i) => (
                <motion.div
                  key={product._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Top Vendors ───────────────────────────────────────────────────────── */}
      <section className="home-section home-section-alt">
        <div className="container">
          <div className="home-section-header">
            <div>
              <h2 className="section-title">Meet Our Top Vendors</h2>
              <p className="section-subtitle">Handpicked stores with the best ratings</p>
            </div>
            <Link to="/vendors" className="see-all-link">
              All Vendors <FiChevronRight size={14} />
            </Link>
          </div>

          <div className="vendors-new-grid">
            {displayVendors.map((vendor, i) => (
              <motion.div
                key={vendor._id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.08 }}
              >
                <div className="vendor-card-new">
                  <div className="vendor-card-banner">
                    <img
                      src={`https://picsum.photos/seed/${vendor.bannerSeed || vendor.storeSlug || vendor._id}/400/120`}
                      alt=""
                      loading="lazy"
                    />
                    <div className="vendor-card-logo-circle">
                      {vendor.logo
                        ? <img src={`http://localhost:5000${vendor.logo}`} alt={vendor.storeName} />
                        : <span>{vendor.logoLetter || vendor.storeName?.[0] || 'V'}</span>}
                    </div>
                  </div>
                  <div className="vendor-card-body">
                    <span className="vendor-cat-badge">
                      {vendor.category?.name || vendor.category || 'Store'}
                    </span>
                    <h4 className="vendor-card-name-new">{vendor.storeName}</h4>
                    <div className="vendor-card-meta">
                      <FiStar size={12} fill="#f59e0b" color="#f59e0b" />
                      <span>{(vendor.rating || 0).toFixed(1)}</span>
                      <span className="vendor-meta-dot">·</span>
                      <span>{vendor.totalProducts || 0} products</span>
                    </div>
                  </div>
                  <div className="vendor-card-foot">
                    <Link
                      to={`/vendors/${vendor.storeSlug || vendor._id}`}
                      className="vendor-visit-btn"
                    >
                      Visit Store <FiArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────────── */}
      <section className="how-it-works">
        <div className="container">
          <div className="hiw-header">
            <h2 className="section-title-center">How Fivora Works</h2>
            <p className="section-subtitle-center">Get started in minutes, whether you're buying or selling</p>
            <div className="hiw-tabs">
              <button
                className={`hiw-tab${activeTab === 'buyers' ? ' hiw-tab--active' : ''}`}
                onClick={() => setActiveTab('buyers')}
              >
                🛍️ For Buyers
              </button>
              <button
                className={`hiw-tab${activeTab === 'sellers' ? ' hiw-tab--active' : ''}`}
                onClick={() => setActiveTab('sellers')}
              >
                🏪 For Sellers
              </button>
            </div>
          </div>

          <div className="hiw-steps">
            {(activeTab === 'buyers' ? STEPS_BUYERS : STEPS_SELLERS).map((step, i) => (
              <Fragment key={step.title}>
                {i > 0 && <div className="hiw-connector" aria-hidden="true" />}
                <motion.div
                  className="hiw-step-card"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.09 }}
                >
                  <span className="hiw-step-num">{i + 1}</span>
                  <div className="hiw-step-icon">{step.icon}</div>
                  <h4 className="hiw-step-title">{step.title}</h4>
                  <p className="hiw-step-desc">{step.desc}</p>
                </motion.div>
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────────────── */}
      <section className="home-section">
        <div className="container">
          <div className="testimonials-header">
            <h2 className="section-title-center">What Our Community Says</h2>
            <p className="section-subtitle-center">Trusted by buyers and vendors across Pakistan</p>
          </div>

          <div className="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.id}
                className="testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <div className="testimonial-stars">
                  {[...Array(t.rating)].map((_, si) => (
                    <FiStar key={si} size={14} fill="#f59e0b" color="#f59e0b" />
                  ))}
                </div>
                <blockquote className="testimonial-quote">"{t.quote}"</blockquote>
                <div className="testimonial-author">
                  <img
                    src={`https://picsum.photos/seed/${t.avatar}/48/48`}
                    alt={t.name}
                    className="testimonial-avatar"
                  />
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-role">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Vendor CTA ────────────────────────────────────────────────────────── */}
      <section className="vendor-cta">
        <div className="container">
          <motion.div
            className="vendor-cta-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="vendor-cta-content">
              <h2 className="vendor-cta-heading">Ready to Grow Your Business?</h2>
              <p className="vendor-cta-sub">
                Join 500+ vendors already earning on Fivora. No hidden fees, no hassle.
              </p>
              <ul className="vendor-cta-bullets">
                {[
                  "Set up your store in minutes — it's free",
                  'Access to millions of buyers across Pakistan',
                  'Real-time analytics & sales insights',
                  'Dedicated seller support team',
                ].map((b) => (
                  <li key={b} className="vendor-cta-bullet">
                    <FiCheck size={14} className="vendor-cta-check" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/vendor/apply" className="btn btn-primary btn-lg vendor-cta-btn">
                Open Your Store Free <FiArrowRight size={16} />
              </Link>
            </div>
            <div className="vendor-cta-visual" aria-hidden="true">
              <div className="vendor-cta-glow" />
              <span className="vendor-cta-emoji">🏪</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Newsletter ────────────────────────────────────────────────────────── */}
      <section className="newsletter-section">
        <div className="container">
          <motion.div
            className="newsletter-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
          >
            <h2 className="newsletter-title">Stay in the Loop</h2>
            <p className="newsletter-sub">
              Get exclusive deals, new arrivals, and vendor announcements straight to your inbox.
            </p>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <div className="newsletter-input-wrap">
                <FiMail className="newsletter-icon" aria-hidden="true" />
                <input
                  type="email"
                  className="form-input newsletter-input"
                  placeholder="Enter your email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <button type="submit" className="btn btn-primary newsletter-btn">
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
